import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { camelCase } from 'lodash';
import { useSelector } from 'react-redux';
import { PercentBar } from 'spw-components/collab/percent-bar';
import {
  CUSTOM_SUBCATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import {
  BenchmarkReportCardType,
  BenchmarkReportIconType,
  BenchmarkReportSubcategoryAnalysis,
} from 'spw-redux/modules/benchmark-reports/types';
import { Row } from 'spw-shared/globalComponents';
import { getBalancedRoundedPercentages, asUsf } from 'spw-shared/utils';
import styled from 'styled-components';
import { buildColorGradient } from 'spw-projects/project-profile/space-categories-and-colors/utils';
import { colors } from 'spw-theme';
import {
  selectCurrentBenchmarkReportCategoriesMap,
  selectCurrentBenchmarkReportIconsMap,
} from 'spw-redux/modules/benchmark-reports/selectors';
import useUnitConversion from 'spw-components/unit-conversion/hooks';

const AnalysisRowContainer = styled.div`
  display: flex;
  align-items: flex-end;

  .icon {
    width: 48px;
    margin-right: 40px;
    margin-bottom: 6px;
  }

  .info {
    flex: 1;
  }

  .label {
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    text-transform: uppercase;
    margin-bottom: 4px;
    color: ${colors.fontColor};
  }

  .percentage {
    text-align: right;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    width: 78px;
    margin-right: 16px;
  }

  .bar-label {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    text-transform: uppercase;
    margin-right: 8px;
    color: ${colors.fontColor};
  }

  .avg-usf {
    text-align: right;
    font-family: Roboto;
    font-size: 32px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    width: 80px;
  }

  .avg-usf-label {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    text-transform: uppercase;
    margin-left: 8px;
    color: ${colors.fontColor};
  }
`;

const AnalysisRow = ({
  iconName,
  label,
  percentage,
  avgUSF,
  color,
  percentageIsNonZeroSmallValue,
}: {
  iconName: string;
  label: string;
  percentage: number;
  percentageIsNonZeroSmallValue: boolean;
  avgUSF: number;
  color: string;
}) => {
  const { getUnit, convertArea } = useUnitConversion();

  const testingPrefix = `iw_analysis_${label.toLowerCase().replace(/ /g, '_')}`;
  return (
    <AnalysisRowContainer>
      <img
        src={`/images/space-icons/${iconName}.svg`}
        alt="category"
        className="icon"
      />
      <div className="info">
        <div className="label">{label}</div>
        <Row alignItems="center" justifyContent="space-between">
          <Row alignItems="center">
            <div
              className="percentage"
              data-testid={`${testingPrefix}_seatmix_percentage`}
            >
              {percentageIsNonZeroSmallValue ? '<1' : percentage}%
            </div>
            <div className="bar-label">Seat Mix</div>
            <PercentBar
              percentage={percentage}
              width="172px"
              height="24px"
              completionColor={color}
              backgroundColor="#DDDDDD"
              testingId={`${testingPrefix}_percentage_bar`}
            />
          </Row>
          <Row alignItems="center">
            <div className="avg-usf" data-testid={`${testingPrefix}_avg_usf`}>
              {asUsf(Math.round(convertArea(avgUSF)))}
            </div>
            <div className="avg-usf-label">Avg SQ {getUnit('linear')}</div>
          </Row>
        </Row>
      </div>
    </AnalysisRowContainer>
  );
};

type Props = {
  individualWorkspacesAnalysis: BenchmarkReportSubcategoryAnalysis[];
};

const DEFAULT_SUBCATEGORIES = ['workstations', 'private_offices'];
const SUBCATEGORY_ORDER = [
  'workstations',
  'private_offices',
  'custom_subcategory',
];

const cardType = BenchmarkReportCardType.individualWorkspacesAnalysis;

function IndividualWorkspacesAnalysis({ individualWorkspacesAnalysis }: Props) {
  const currentReportIconsMap = useSelector(
    selectCurrentBenchmarkReportIconsMap
  );

  const currentReportCategories = useSelector(
    selectCurrentBenchmarkReportCategoriesMap
  );

  const analysisWithBalancedPercentages = getBalancedRoundedPercentages<
    BenchmarkReportSubcategoryAnalysis
  >(
    individualWorkspacesAnalysis?.map((analysis) => {
      return {
        ...analysis,
        percentage: analysis.seatMix * 100,
        id: analysis.subcategory,
      };
    }) || []
  );

  const colorGradient = useMemo(() => {
    return buildColorGradient(
      currentReportCategories?.[INDIVIDUAL_WORKSPACES_CATEGORY]?.color
    );
  }, [currentReportCategories]);

  const subcategoryColors: Record<string, string> = useMemo(() => {
    const workstationsIndex =
      currentReportCategories[WORKSTATIONS_SUBCATEGORY].gradientColorIndex;
    const privateOfficesIndex =
      currentReportCategories[PRIVATE_OFFICES_SUBCATEGORY].gradientColorIndex;
    const customSubcategoryIndex =
      currentReportCategories[CUSTOM_SUBCATEGORY].gradientColorIndex;

    return {
      [WORKSTATIONS_SUBCATEGORY]: colorGradient[workstationsIndex],
      [PRIVATE_OFFICES_SUBCATEGORY]: colorGradient[privateOfficesIndex],
      [CUSTOM_SUBCATEGORY]: colorGradient[customSubcategoryIndex],
    };
  }, [colorGradient, currentReportCategories]);

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <div className="title">INDIVIDUAL WORKSPACES ANALYSIS</div>
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="space-evenly"
      >
        {SUBCATEGORY_ORDER.map((subcategory) => {
          const analysis = analysisWithBalancedPercentages.find(
            (a) => a?.id === subcategory
          );

          let label = '';

          if (DEFAULT_SUBCATEGORIES.includes(analysis?.subcategory)) {
            label = analysis.name;
          } else {
            label = currentReportCategories[CUSTOM_SUBCATEGORY].name;
          }

          if (
            analysis?.percentage === 0 &&
            subcategory === 'custom_subcategory'
          )
            return null;
          return (
            analysis && (
              <Box
                key={analysis.subcategory}
                data-cy={`individual_workspaces_analysis_${analysis.subcategory}`}
              >
                <AnalysisRow
                  label={label}
                  avgUSF={analysis.averageSquareFeet}
                  percentage={analysis.percentage}
                  percentageIsNonZeroSmallValue={
                    analysis.valueIsGreaterThanZeroButLessThanOne
                  }
                  iconName={
                    currentReportIconsMap[cardType][
                      analysis.subcategory as BenchmarkReportIconType
                    ] ||
                    currentReportIconsMap[cardType][
                      BenchmarkReportIconType.otherWorkpoints
                    ]
                  }
                  color={subcategoryColors[camelCase(analysis.subcategory)]}
                />
              </Box>
            )
          );
        })}
      </Box>
    </Box>
  );
}

export default IndividualWorkspacesAnalysis;
